<template>
    <div class="col-12 mx-auto">
        <div class="card">
            <div class="card-header" v-if="showSettingsBtn">

                <i class="fas fa-cogs pointer fa-2x float-right text-primary"
                   v-if="showSettingsBtn"
                   @click.stop="changeGridSettings"
                   data-toggle="tooltip"
                   :title="settingsTooltipText"></i>

                <grid-user-settings ref="userSettings"
                                    :schemaModelFields="schemaModelFields"
                                    :componentName="componentName"
                                    :useLabelAsTitle="true"
                                    :showUserSettings="true"
                                    :gridOptions="userSettings"
                                    v-if="showUserSettingsForm"
                                    @saveGridSettings="saveGridSettings"
                                    @loadGridSettings="loadGridSettings"
                                    @deleteGridSettings="deleteGridSettings"
                                    @userSettingsChanged="userSettingsChanged"></grid-user-settings>
            </div>

            <div class="btn-toolbar justify-content-between mt-3" role="toolbar">
                <div class="input-group" :class="showSettingsBtn? 'col-lg-6 col-md-12':'col-12'">
                    <input v-model="searchQuery"
                           v-on:keyup.enter.stop="massSearch"
                           v-on:keyup.esc.stop="clearFilters"
                           type="text"
                           class="form-control"
                           aria-label="Mass search input"
                           aria-describedby="massSearch"/>
                    <button class="btn btn-secondary" @click.stop="massSearch">
                        <i class="fas fa-search"></i>
                        {{ $t('buttons.search.btnText') }}
                    </button>
                    <button class="btn btn-secondary" @click.stop="clearFilters">
                        <i class="fas fa-times"></i>
                        {{ $t('buttons.clearFilters.btnText') }}
                    </button>
                </div>
            </div>

            <div class="card-body" id="grid">
                <kendo-datasource ref="datasource"
                                  :transport="transportSettings"
                                  :schema-model-id="'id'"
                                  :schema-model-fields="schemaModelFields"
                                  :schema-total="'total'"
                                  :schema-data="'data'"
                                  :page-size="defaultGridRows"
                                  :server-paging="true"
                                  :server-sorting="true"
                                  :server-filtering="true"
                                  :error="dataSourceError"
                                  :requestEnd="datasourceRequestEnd"></kendo-datasource>

                <kendo-grid ref="grid"
                            :data-source-ref="'datasource'"
                            :navigatable="gridOptions.navigatable"
                            :editable="gridOptions.editable"
                            :filterable="gridOptions.filterable"
                            :pageable="gridOptions.pageable"
                            :reorderable="gridOptions.reorderable"
                            :resizable="gridOptions.resizable"
                            :groupable="gridOptions.groupable"
                            :allow-copy="gridOptions.allowCopy"
                            :column-menu="gridOptions.columnMenu"
                            :sortable-mode="gridOptions.sortable.mode"
                            :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                            :sortable-show-indexes="gridOptions.sortable.showIndexes"
                            :selectable="gridOptions.selectable"
                            :scrollable="gridOptions.scrollable"
                            :toolbar="toolbar"
                            :excel="gridOptions.excel"
                            :data-bound="dataBound">
                    <kendo-grid-column :field="'id'" :title="$t('eventSubscriptions.gridHeaders.id')" :width="150"
                                       :hidden="hiddenColumns && hiddenColumns.includes('id')"></kendo-grid-column>
                    <kendo-grid-column :field="'eventId'" :title="$t('eventSubscriptions.gridHeaders.eventId')"
                                       :width="150"
                                       :hidden="hiddenColumns && hiddenColumns.includes('eventId')"></kendo-grid-column>
                    <kendo-grid-column :field="'eventName'" :title="$t('eventSubscriptions.gridHeaders.eventName')"
                                       :width="500"
                                       :hidden="hiddenColumns && hiddenColumns.includes('eventName')"></kendo-grid-column>
                    <kendo-grid-column :field="'eventStartDate'"
                                       :title="$t('eventSubscriptions.gridHeaders.eventStartDate')"
                                       :width="150"
                                       :hidden="hiddenColumns && hiddenColumns.includes('eventStartDate')"
                                       :format="'{0:dd.MM.yyyy}'"
                                       :template="datetimeDisplayTemplateEventStartDate"></kendo-grid-column>
                    <kendo-grid-column :field="'eventEndDate'"
                                       :title="$t('eventSubscriptions.gridHeaders.eventEndDate')"
                                       :width="150"
                                       :hidden="hiddenColumns && hiddenColumns.includes('eventEndDate')"
                                       :format="'{0:dd.MM.yyyy}'"
                                       :template="datetimeDisplayTemplateEventEndDate"></kendo-grid-column>
                    <kendo-grid-column :field="'eventSubscriptionStartDate'"
                                       :title="$t('eventSubscriptions.gridHeaders.eventSubscriptionStartDate')"
                                       :width="150"
                                       :hidden="hiddenColumns && hiddenColumns.includes('eventSubscriptionStartDate')"
                                       :format="'{0:dd.MM.yyyy}'"
                                       :template="datetimeDisplayTemplateEventSubscriptionStartDate"></kendo-grid-column>
                    <kendo-grid-column :field="'eventSubscriptionEndDate'"
                                       :title="$t('eventSubscriptions.gridHeaders.eventSubscriptionEndDate')"
                                       :width="150"
                                       :hidden="hiddenColumns && hiddenColumns.includes('eventSubscriptionEndDate')"
                                       :format="'{0:dd.MM.yyyy}'"
                                       :template="datetimeDisplayTemplateEventSubscriptionEndDate"></kendo-grid-column>
                    <kendo-grid-column :field="'notificationTypeName'"
                                       :title="$t('eventSubscriptions.gridHeaders.notificationTypeName')" :width="400"
                                       :hidden="hiddenColumns && hiddenColumns.includes('notificationTypeName')"></kendo-grid-column>
                    <kendo-grid-column :field="'userName'" :title="$t('eventSubscriptions.gridHeaders.userName')"
                                       :width="300"
                                       :hidden="hiddenColumns && hiddenColumns.includes('userName')"></kendo-grid-column>
                    <kendo-grid-column :field="'email'" :title="$t('eventSubscriptions.gridHeaders.email')" :width="300"
                                       :hidden="hiddenColumns && hiddenColumns.includes('email')"></kendo-grid-column>
                    <kendo-grid-column :field="'note'" :title="$t('eventSubscriptions.gridHeaders.note')" :width="300"
                                       :hidden="hiddenColumns && hiddenColumns.includes('note')"></kendo-grid-column>
                    <kendo-grid-column :field="'createdAt'"
                                       :title="$t('eventSubscriptions.gridHeaders.createdAt')"
                                       :width="200"
                                       :hidden="hiddenColumns && hiddenColumns.includes('createdAt')"
                                       :format="'{0:dd.MM.yyyy HH:mm}'"
                                       :template="datetimeDisplayTemplateCreatedAt"></kendo-grid-column>

                </kendo-grid>
            </div>
        </div>

    </div>
</template>

<script src="https://kendo.cdn.telerik.com/2019.2.619/js/pako_deflate.min.js"></script>

<script>
    import $ from 'jquery'; // това се изисква за да се вмъкват kendo ui компоненти. Виж метод parentIdDropDownEditor
    import Vue from 'vue';
    import JSZip from 'jszip'
    import GridUserSettings from "../../components/grid/UserSettings";
    import DatetimeCellDisplayTemplate from "../../components/grid/DatetimeCellDisplayTemplate";
    import {mapGetters} from 'vuex';
    import personalizationService from '../../services/personalization.service';

    export default {
        name: 'event-subscriptions-grid',
        components: {
            'grid-user-settings': GridUserSettings
        },
        created: function () {
            window.JSZip = JSZip // ���������� � �� �� ������ �������� � excel
        },
        props: {
            // ����������, ����� ������� �� ���������� � �������� �� ������� �����������
            size: {
                default: 6,
                type: Number
            },
            showSettingsBtn: {
                default: true,
                type: Boolean
            },
            relatedEventId: {
                default: 0,
                type: [Number, String]
            },
            hiddenColumns: {
                default: [],
                type: Array
            },

        },
        data() {
            return {
                componentName: "EventSubscriptionsGrid", // ������ �� ������������ �� �������� �� ������� �����������
                settingsTooltipText: this.$t('buttons.settings.tooltipText'),
                customSettingsLoaded: false,
                titleKey: 'sideMenu.eventSubscriptions',
                searchQuery: '',
                defaultGridRows: this.$store.getters.defaultGridRows,
                schemaModelFields: {
                    id: {name: 'id', type: 'number', editable: false, nullable: false, visible: true},
                    eventId: {name: 'eventId', type: 'number', editable: false, nullable: false, visible: true},
                    eventName: {name: 'eventName', type: 'string', editable: false, nullable: true, visible: true},
                    eventStartDate: {
                        name: 'eventStartDate',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventEndDate: {name: 'eventEndDate', type: 'date', editable: false, nullable: true, visible: true},
                    eventSubscriptionStartDate: {
                        name: 'eventSubscriptionStartDate',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    eventSubscriptionEndDate: {
                        name: 'eventSubscriptionEndDate',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    notificationTypeName: {
                        name: 'notificationTypeName',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    userName: {name: 'userName', type: 'string', editable: false, nullable: true, visible: true},
                    email: {name: 'email', type: 'string', editable: false, nullable: true, visible: true},
                    note: {name: 'note', type: 'string', editable: false, nullable: true, visible: true},
                    createdAt: {
                        name: 'createdAt',
                        from: 'createdAtUtc',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },

                },
                gridOptions: {
                    ...this.$store.getters.gridOptions,
                    toolbar2: ['excel'],
                    excel: {
                        fileName: 'EventSubscriptions.xlsx',
                        allPages: false,
                        filterable: true,
                        collapsible: true
                    },
                },
                working: false,
                showUserSettingsForm: false,
            }
        },
        methods: {
            dataBound() {
                if (!this.customSettingsLoaded) {
                    this.customSettingsLoaded = true;
                    this.loadGridSettings();
                }

            },
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration,
                });
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return {models: kendo.stringify(options.models)}
                } else {
                    // ������ POST action �� �������� �� �������������� � ������ �� �������������
                    // options. ���� �� ������� ���������� �� take, skip , page � ������ � ������
                    // �������� �����������
                    this.saveState();

                    let newOptions = {eventId: this.relatedEventId, request: options};
                    return kendo.stringify(newOptions);
                }
            },
            //This is needed for grid state save and mass search
            getGridOptions() {
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    return grid.getOptions();
                } else {
                    return null;
                }
            },
            saveGridSettings() {
                const gridSettings = this.getGridOptions();
                if (gridSettings) {
                    this.$refs.userSettings.setGridSettings(gridSettings);

                    personalizationService
                        .saveGridSettings(this.componentName, gridSettings)
                        .then(response => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                                text: this.$helpers.responseMessage(response),
                                color: 'success',
                                time: this.$store.getters.notificationDuration
                            });
                        })
                        .catch(error => {
                            this.$vs.notify({
                                title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                text: this.$helpers.errorText(error),
                                color: this.$helpers.errorColor(error),
                                time: this.$store.getters.notificationDuration
                            });
                        });
                }
            },
            loadGridSettings() {
                personalizationService
                    .loadGridSettings(this.componentName)
                    .then(response => {
                        try {
                            var options = personalizationService.mergePersonGridSettings(this.getGridOptions(), response.data.settings);

                            let grid = this.$refs.grid.kendoWidget();
                            if (options && grid) {
                                grid.setOptions(options);

                                if (options.pageable.size != grid.dataSource.options.pageSize) {
                                    grid.dataSource.pageSize(options.pageable.size);
                                    grid.refresh();
                                }

                                const userSettingsEditor = this.$refs.userSettings;
                                if (userSettingsEditor) {
                                    userSettingsEditor.setGridSettings(grid.getOptions());
                                }
                            }

                            this.customSettingsLoaded = true;
                        } catch (e) {
                            this.customSettingsLoaded = true;
                        }
                    })
                    .catch(error => {
                        this.customSettingsLoaded = true;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            deleteGridSettings() {
                personalizationService
                    .delteGridSettings(this.componentName)
                    .then(response => {
                        this.$router.go();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            userSettingsChanged(userSettings, pageSizeChanged) {
                let grid = this.$refs.grid.kendoWidget();

                if (grid) {
                    try {
                        grid.setOptions(userSettings);
                        if (pageSizeChanged === true) {
                            grid.dataSource.pageSize(userSettings.pageable.size);
                            grid.refresh();
                        }
                    } catch (e) {
                        return;
                    }
                }
            },
            saveState: function () {
                let grid = this.$refs.grid.kendoWidget();

                localStorage['' + this.componentName + "-grid-options"] = kendo.stringify(grid.getOptions());
            },
            loadState: function () {
                let options = ['' + this.componentName + "-grid-options"];
                let grid = this.$refs.grid.kendoWidget();

                if (options) {
                    grid.setOptions(JSON.parse(options));
                }
            },
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            changeGridSettings() {
                this.showUserSettingsForm = !this.showUserSettingsForm;
            },
            // ������ �� ������������ �� �������� �� ������� �����������. ������� ��������� �� ���������� � �������� �� ��������� ��������(��������)
            resize(componentName, size) {
                this.$emit('resize', componentName, size, this.$refs.grid.kendoWidget())
            },
            // ������ �� ������������ �� �������� �� ������� �����������. ������� ��������� �� ���������� � �������� �� ��������� ��������(��������)
            hide(componentName) {
                this.$emit('hide', componentName)
            },
            datasourceRequestEnd() {
            },
            datetimeDisplayTemplate(date, useShortFormat) {
                return {
                    template: Vue.component(DatetimeCellDisplayTemplate.name, DatetimeCellDisplayTemplate),
                    templateArgs: {
                        dateValue: date,
                        showShortDate: useShortFormat
                    },
                };
            },
            datetimeDisplayTemplateEventStartDate: function (e) {
                return this.datetimeDisplayTemplate(e.eventStartDate, true);
            },
            datetimeDisplayTemplateEventEndDate: function (e) {
                return this.datetimeDisplayTemplate(e.eventEndDate, true);
            },
            datetimeDisplayTemplateEventSubscriptionStartDate: function (e) {
                return this.datetimeDisplayTemplate(e.eventSubscriptionStartDate, true);
            },
            datetimeDisplayTemplateEventSubscriptionEndDate: function (e) {
                return this.datetimeDisplayTemplate(e.eventSubscriptionEndDate, true);
            },
            datetimeDisplayTemplateCreatedAt: function (e) {
                return this.datetimeDisplayTemplate(e.createdAt, false);
            },

            refresh() {
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.read();
                }
            },
            clearFilters() {
                this.searchQuery = '';
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.sort({});
                    grid.dataSource.page(1);
                    grid.dataSource.aggregate([]);
                    grid.dataSource.filter({});
                }
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    })

                    return;
                }

                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
        },
        mounted() {

        },
        computed: {
            token: function () {
                return this.$store.getters.authToken;
            },
            userSettings() {
                let grid = this.$refs.grid.kendoWidget();
                return grid.getOptions();
            },
            filter() {
                let eventId = Number(this.relatedEventId);
                if (isNaN(eventId) || eventId <= 0) {
                    return {};
                }

                return {
                    logic: "and", filters: [
                        {
                            field: 'EventId',
                            operator: 'eq',
                            value: this.relatedEventId
                        }]
                }
            },
            toolbar: function () {
                return [{name: 'excel', text: this.$t('buttons.excelExport.tooltipText')}]
            },
            transportSettings() {

                return {
                    read: {
                        url: this.$store.getters.baseUrl + '/api/eventSubscriptions/eventSubscriptionsList',
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                }
            },
            ...mapGetters(['claims'])
        }
    }
</script>

<style>

    .k-grid .k-grid-header .k-header .k-link {
        height: auto;
    }

    .k-grid .k-grid-header .k-header {
        white-space: normal;
    }

    .k-grid-details,
    .k-grid-excel {
        padding: 0;
        margin-bottom: 2px;
    }
</style>
